import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { usePlan } from '../contexts/PlanContext';
import {
  Image, Film, BookOpen, LayoutGrid, FileText, Mail, Newspaper, Printer,
  Loader2, Check, ArrowRight, Sparkles, Zap, UserCircle,
  Home, DollarSign, CalendarDays, Clock, FileCheck, Trophy, X
} from 'lucide-react';

const ASSET_TYPES = [
  {
    id: 'post',
    label: 'Social Post',
    desc: '1080x1080 — Instagram, Facebook feed',
    icon: Image,
    color: '#3B82F6',
    gradient: 'from-blue-500 to-blue-600',
    bgLight: 'bg-blue-50',
    category: 'social',
  },
  {
    id: 'story',
    label: 'Story',
    desc: '1080x1920 — Instagram & Facebook Stories',
    icon: BookOpen,
    color: '#8B5CF6',
    gradient: 'from-violet-500 to-purple-600',
    bgLight: 'bg-violet-50',
    category: 'social',
  },
  {
    id: 'reel',
    label: 'Reel / Video',
    desc: '1080x1920 — Short-form video with music',
    icon: Film,
    color: '#EC4899',
    gradient: 'from-pink-500 to-rose-600',
    bgLight: 'bg-pink-50',
    category: 'social',
  },
  {
    id: 'carousel',
    label: 'Carousel',
    desc: '1080x1080 — Multi-slide swipeable post',
    icon: LayoutGrid,
    color: '#F59E0B',
    gradient: 'from-amber-500 to-orange-500',
    bgLight: 'bg-amber-50',
    category: 'social',
  },
  {
    id: 'flyer',
    label: 'Flyer',
    desc: '8.5x11" — Print-ready property flyer',
    icon: FileText,
    color: '#10B981',
    gradient: 'from-emerald-500 to-green-600',
    bgLight: 'bg-emerald-50',
    category: 'print',
  },
  {
    id: 'postcard',
    label: 'Postcard',
    desc: '6x4" — Direct mail postcard',
    icon: Mail,
    color: '#F97316',
    gradient: 'from-orange-500 to-red-500',
    bgLight: 'bg-orange-50',
    category: 'print',
  },
  {
    id: 'brochure',
    label: 'Brochure',
    desc: '11x8.5" — Landscape property brochure',
    icon: Printer,
    color: '#06B6D4',
    gradient: 'from-cyan-500 to-teal-600',
    bgLight: 'bg-cyan-50',
    category: 'print',
  },
  {
    id: 'newsletter',
    label: 'Newsletter',
    desc: '600x800 — Email newsletter template',
    icon: Newspaper,
    color: '#6366F1',
    gradient: 'from-indigo-500 to-blue-600',
    bgLight: 'bg-indigo-50',
    category: 'email',
  },
];

const BRANDING_TYPES = [
  {
    id: 'agent_card',
    label: 'Business Card',
    desc: '3.5x2" — Professional agent card',
    icon: UserCircle,
    color: '#0F766E',
    gradient: 'from-teal-600 to-emerald-700',
    bgLight: 'bg-teal-50',
    category: 'branding',
  },
  {
    id: 'social_banner',
    label: 'Social Banner',
    desc: '1500x500 — Facebook/LinkedIn cover',
    icon: Image,
    color: '#7C3AED',
    gradient: 'from-violet-600 to-purple-700',
    bgLight: 'bg-violet-50',
    category: 'branding',
  },
  {
    id: 'email_signature',
    label: 'Email Signature',
    desc: '600x200 — Professional email footer',
    icon: Mail,
    color: '#B45309',
    gradient: 'from-amber-600 to-yellow-700',
    bgLight: 'bg-amber-50',
    category: 'branding',
  },
];

const STORAGE_KEY = 'realprop_recently_used_templates';

// Listing status categories — shown before content creation
const LISTING_STATUSES = [
  { id: 'just_listed', label: 'Just Listed', icon: Home, color: '#10B981', category: 'new_listing', desc: 'New property on the market' },
  { id: 'price_reduced', label: 'Price Reduced', icon: DollarSign, color: '#F59E0B', category: 'price_reduced', desc: 'Price drop announcement', inputType: 'price' },
  { id: 'open_house', label: 'Open House', icon: CalendarDays, color: '#3B82F6', category: 'open_house', desc: 'Showing event', inputType: 'datetime' },
  { id: 'coming_soon', label: 'Coming Soon', icon: Clock, color: '#8B5CF6', category: 'coming_soon', desc: 'Pre-market teaser', inputType: 'date' },
  { id: 'under_contract', label: 'Under Contract', icon: FileCheck, color: '#F97316', category: 'under_contract', desc: 'Pending sale' },
  { id: 'sold', label: 'Sold', icon: Trophy, color: '#EF4444', category: 'sold', desc: 'Closed deal celebration', inputType: 'text' },
];

// Status Picker Modal
const StatusPickerModal = ({ isOpen, onClose, onSelect, assetLabel }) => {
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [extraInput, setExtraInput] = useState('');
  const [extraEnd, setExtraEnd] = useState('');

  if (!isOpen) return null;

  const handlePick = (status) => {
    if (status.inputType) {
      setSelectedStatus(status);
      setExtraInput('');
      setExtraEnd('');
    } else {
      onSelect(status.id, status.category, null);
    }
  };

  // Build a clean human-readable Open House range, e.g. "Sat, Jun 14, 2026 · 1:00 – 3:00 PM"
  const formatOpenHouse = (startIso, endTime) => {
    if (!startIso) return null;
    const start = new Date(startIso);
    if (isNaN(start.getTime())) return startIso;
    const dateStr = start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    const startTimeStr = start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    if (endTime) {
      const [h, m] = endTime.split(':').map(Number);
      const end = new Date(start);
      end.setHours(h, m, 0, 0);
      const endTimeStr = end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      return `${dateStr} · ${startTimeStr} – ${endTimeStr}`;
    }
    return `${dateStr} · ${startTimeStr}`;
  };

  const handleConfirm = () => {
    if (!selectedStatus) return;
    let value = extraInput || null;
    if (selectedStatus.inputType === 'datetime' && extraInput) {
      value = formatOpenHouse(extraInput, extraEnd);
    }
    onSelect(selectedStatus.id, selectedStatus.category, value);
  };

  return (
    <div className="fixed inset-0 bg-white/70 backdrop-blur-2xl z-50 flex items-center justify-center p-4" data-testid="status-picker-modal">
      <div className="bg-white border border-gray-200 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.12)] max-w-2xl w-full p-6 space-y-5 animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="status-picker-title">
              What are you creating?
            </h2>
            <p className="text-sm text-gray-500 mt-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {assetLabel ? `Creating ${assetLabel}` : 'Select listing status'} — we'll pick the best template
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" data-testid="status-picker-close">
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="status-picker-grid">
          {LISTING_STATUSES.map((status) => {
            const Icon = status.icon;
            const isActive = selectedStatus?.id === status.id;
            return (
              <button
                key={status.id}
                onClick={() => handlePick(status)}
                className={`flex flex-col items-center justify-center p-4 border-2 rounded-xl transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${
                  isActive ? 'border-gray-900 bg-gray-50 ring-2 ring-gray-900/10' : 'border-gray-200 hover:border-gray-400'
                }`}
                data-testid={`status-pick-${status.id}`}
              >
                <div className="w-11 h-11 rounded-full flex items-center justify-center mb-2" style={{ backgroundColor: `${status.color}15` }}>
                  <Icon className="h-5 w-5" style={{ color: status.color }} />
                </div>
                <span className="text-sm font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{status.label}</span>
                <span className="text-[11px] text-gray-400 mt-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>{status.desc}</span>
              </button>
            );
          })}
        </div>

        {/* Extra input for statuses that need it */}
        {selectedStatus?.inputType && (
          <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-3" data-testid="status-extra-input">
            <label className="text-sm font-medium text-gray-700">
              {selectedStatus.inputType === 'price' && 'New Price'}
              {selectedStatus.inputType === 'datetime' && 'Open House Date & Time'}
              {selectedStatus.inputType === 'date' && 'Go-Live Date'}
              {selectedStatus.inputType === 'text' && 'Sale Details (e.g. "Over asking!")'}
            </label>
            {selectedStatus.inputType === 'datetime' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">Starts</span>
                  <Input
                    type="datetime-local"
                    value={extraInput}
                    onChange={(e) => setExtraInput(e.target.value)}
                    className="h-10"
                    data-testid="status-extra-value"
                  />
                </div>
                <div className="space-y-1">
                  <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">Ends</span>
                  <Input
                    type="time"
                    value={extraEnd}
                    onChange={(e) => setExtraEnd(e.target.value)}
                    className="h-10"
                    data-testid="status-extra-end-value"
                  />
                </div>
              </div>
            ) : (
              <Input
                type={selectedStatus.inputType === 'date' ? 'date' : 'text'}
                placeholder={selectedStatus.inputType === 'price' ? '$649,000' : ''}
                value={extraInput}
                onChange={(e) => setExtraInput(e.target.value)}
                className="h-10"
                data-testid="status-extra-value"
              />
            )}
            {selectedStatus.inputType === 'datetime' && extraInput && (
              <p className="text-xs text-gray-500" data-testid="status-openhouse-preview">
                {formatOpenHouse(extraInput, extraEnd)}
              </p>
            )}
            <Button onClick={handleConfirm} className="w-full bg-black hover:bg-gray-900 text-white" data-testid="status-confirm-btn">
              <Sparkles className="h-4 w-4 mr-2" />
              Create {assetLabel || 'Content'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

function getRecentlyUsed() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

function addRecentlyUsed(templateId) {
  const recent = getRecentlyUsed();
  const updated = [templateId, ...recent.filter(id => id !== templateId)].slice(0, 30);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  return updated;
}

export const AssetTypeHub = ({ activeListing, templates }) => {
  const navigate = useNavigate();
  const { handleApiError } = usePlan() || {};
  const [selected, setSelected] = useState(new Set());
  const [loadingType, setLoadingType] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);

  // Status picker state
  const [showStatusPicker, setShowStatusPicker] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // { mode: 'single'|'batch', typeId?, types? }

  const toggleSelect = useCallback((typeId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(typeId)) {
        next.delete(typeId);
      } else {
        next.add(typeId);
      }
      return next;
    });
  }, []);

  const navigateToEditor = useCallback((template, type, listingStatus, statusExtra) => {
    const stateData = { propertyData: activeListing, fromMagicCreate: true, listingStatus, statusExtra };
    if (type === 'reel') {
      navigate(`/reel-editor?template=${template.template_id || template.reel_template_id}`, {
        state: { ...stateData, reelTemplate: template }
      });
      return;
    }
    if (type === 'carousel') {
      navigate(`/carousel-editor?property=${encodeURIComponent(JSON.stringify({ ...activeListing, listingStatus, statusExtra }))}`, {
        state: stateData
      });
      return;
    }
    if (type === 'newsletter') {
      navigate('/email-lists', { state: stateData });
      return;
    }
    const isPrint = ['flyer', 'postcard', 'brochure'].includes(type);
    navigate(`/create-campaign/${template.template_id}`, {
      state: { ...stateData, isPrint }
    });
  }, [activeListing, navigate]);

  // Open status picker before creating content
  const handleSingleCreate = useCallback((typeId) => {
    setPendingAction({ mode: 'single', typeId });
    setShowStatusPicker(true);
  }, []);

  // Actually create after status is selected
  const executeCreate = useCallback(async (typeId, statusCategory, statusExtra) => {
    setLoadingType(typeId);
    try {
      const recentlyUsed = getRecentlyUsed();
      const res = await apiClient.post('/magic-create/auto-select-template', {
        type: typeId,
        exclude_ids: recentlyUsed,
        category: statusCategory,
      });

      if (res.data.success && res.data.template) {
        const tpl = res.data.template;
        addRecentlyUsed(tpl.template_id);
        toast.success(`Template "${tpl.name}" selected for ${typeId}`);
        navigateToEditor(tpl, typeId, statusCategory, statusExtra);
      } else {
        // Fallback for types without DB templates
        if (typeId === 'carousel') {
          navigateToEditor({}, 'carousel', statusCategory, statusExtra);
          return;
        }
        if (typeId === 'newsletter') {
          navigateToEditor({}, 'newsletter', statusCategory, statusExtra);
          return;
        }
        toast.error(res.data.error || `No templates found for ${typeId}`);
      }
    } catch (err) {
      if (typeId === 'carousel') {
        navigateToEditor({}, 'carousel', statusCategory, statusExtra);
        return;
      }
      if (typeId === 'newsletter') {
        navigateToEditor({}, 'newsletter', statusCategory, statusExtra);
        return;
      }
      toast.error('Failed to auto-select template');
    } finally {
      setLoadingType(null);
    }
  }, [navigateToEditor, handleApiError]);

  const handleBatchCreate = useCallback(() => {
    if (selected.size === 0) {
      toast.error('Select at least one asset type');
      return;
    }
    setPendingAction({ mode: 'batch', types: Array.from(selected) });
    setShowStatusPicker(true);
  }, [selected]);

  const executeBatchCreate = useCallback(async (types, statusCategory, statusExtra) => {
    // Carousel still goes through the dedicated editor route (it's stored in
    // its own `carousel_compositions` collection, not as a campaign template).
    // Newsletter, post, story, reel, flyer, postcard, brochure all flow
    // through batch-create-drafts so they show up as proper drafts in the
    // dashboard alongside the rest.
    const specialTypes = types.filter(t => ['carousel'].includes(t));
    const batchTypes = types.filter(t => !['carousel'].includes(t));

    setBatchLoading(true);
    try {
      if (batchTypes.length > 0) {
        const recentlyUsed = getRecentlyUsed();
        const res = await apiClient.post('/magic-create/batch-create-drafts', {
          types: batchTypes,
          property_data: { ...activeListing, listingStatus: statusCategory, statusExtra },
          exclude_ids: recentlyUsed,
          category: statusCategory,
        });

        if (res.data.success) {
          const newUsed = res.data.used_template_ids || [];
          newUsed.forEach(id => addRecentlyUsed(id));
          const successCount = res.data.results.filter(r => r.success).length;
          const failedTypes = (res.data.results || [])
            .filter(r => !r.success)
            .map(r => r.type);
          if (successCount > 0) {
            toast.success(`Created ${successCount} draft${successCount !== 1 ? 's' : ''} successfully!`);
          }
          if (failedTypes.length > 0) {
            toast.error(`Couldn't create ${failedTypes.join(', ')} — no template available for that type/category yet.`);
          }
        }
      }

      if (specialTypes.length > 0) {
        toast.info(`${specialTypes.join(', ')} opened separately — check your editors.`);
      }

      // Navigate to campaigns/dashboard to see drafts
      if (batchTypes.length > 0) {
        navigate('/campaigns');
      } else if (specialTypes.includes('carousel')) {
        navigate(`/carousel-editor?property=${encodeURIComponent(JSON.stringify(activeListing))}`);
      }
    } catch (err) {
      if (handleApiError && handleApiError(err)) {
        setBatchLoading(false);
        return;
      }
      toast.error('Batch creation failed');
    } finally {
      setBatchLoading(false);
    }
  }, [selected, activeListing, navigate]);

  const socialTypes = ASSET_TYPES.filter(t => t.category === 'social');
  const printTypes = ASSET_TYPES.filter(t => t.category === 'print');
  const emailTypes = ASSET_TYPES.filter(t => t.category === 'email');
  const brandingTypes = BRANDING_TYPES;

  // Status picker callback — routes to single or batch
  const handleStatusSelect = useCallback((statusId, statusCategory, statusExtra) => {
    setShowStatusPicker(false);
    if (!pendingAction) return;

    if (pendingAction.mode === 'single') {
      executeCreate(pendingAction.typeId, statusCategory, statusExtra);
    } else if (pendingAction.mode === 'batch') {
      executeBatchCreate(pendingAction.types, statusCategory, statusExtra);
    }
    setPendingAction(null);
  }, [pendingAction, executeCreate, executeBatchCreate]);

  // Get the label for the pending action
  const pendingAssetLabel = pendingAction?.mode === 'single'
    ? [...ASSET_TYPES, ...BRANDING_TYPES].find(a => a.id === pendingAction.typeId)?.label
    : pendingAction?.mode === 'batch'
      ? `${pendingAction.types.length} items`
      : '';

  return (
    <div className="max-w-6xl mx-auto" data-testid="asset-type-hub">
      {/* Status Picker Modal */}
      <StatusPickerModal
        isOpen={showStatusPicker}
        onClose={() => { setShowStatusPicker(false); setPendingAction(null); }}
        onSelect={handleStatusSelect}
        assetLabel={pendingAssetLabel}
      />
      {/* Section header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-black text-white text-sm flex items-center justify-center font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>2</div>
          <div>
            <h3 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Content Creation Hub
            </h3>
            <p className="text-sm text-gray-500 mt-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Pick one to create instantly, or select multiple and batch generate
            </p>
          </div>
        </div>
        {selected.size > 0 && (
          <Button
            onClick={handleBatchCreate}
            disabled={batchLoading}
            className="bg-black hover:bg-gray-900 text-white gap-2 px-6 h-11 rounded-full shadow-lg hover:shadow-xl transition-all"
            data-testid="batch-generate-btn"
          >
            {batchLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Zap className="h-4 w-4" />
            )}
            Generate {selected.size} Selected
          </Button>
        )}
      </div>

      {/* Social Media */}
      <div className="mb-8">
        <p className="text-xs tracking-widest uppercase font-bold text-gray-400 mb-4 pl-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Social Media
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {socialTypes.map(asset => (
            <AssetCard
              key={asset.id}
              asset={asset}
              isSelected={selected.has(asset.id)}
              isLoading={loadingType === asset.id}
              onToggle={() => toggleSelect(asset.id)}
              onCreate={() => handleSingleCreate(asset.id)}
            />
          ))}
        </div>
      </div>

      {/* Print */}
      <div className="mb-8">
        <p className="text-xs tracking-widest uppercase font-bold text-gray-400 mb-4 pl-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Print Materials
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {printTypes.map(asset => (
            <AssetCard
              key={asset.id}
              asset={asset}
              isSelected={selected.has(asset.id)}
              isLoading={loadingType === asset.id}
              onToggle={() => toggleSelect(asset.id)}
              onCreate={() => handleSingleCreate(asset.id)}
            />
          ))}
        </div>
      </div>

      {/* Email & Branding */}
      <div className="mb-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <p className="text-xs tracking-widest uppercase font-bold text-gray-400 mb-4 pl-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Email
          </p>
          <div className="grid grid-cols-1 gap-4">
            {emailTypes.map(asset => (
              <AssetCard
                key={asset.id}
                asset={asset}
                isSelected={selected.has(asset.id)}
                isLoading={loadingType === asset.id}
                onToggle={() => toggleSelect(asset.id)}
                onCreate={() => handleSingleCreate(asset.id)}
              />
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs tracking-widest uppercase font-bold text-gray-400 mb-4 pl-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Agent Branding
          </p>
          <div className="grid grid-cols-1 gap-4">
            {brandingTypes.map(asset => (
              <AssetCard
                key={asset.id}
                asset={asset}
                isSelected={selected.has(asset.id)}
                isLoading={loadingType === asset.id}
                onToggle={() => toggleSelect(asset.id)}
                onCreate={() => handleSingleCreate(asset.id)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Batch footer - mobile sticky */}
      {selected.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-xl border-t border-gray-200 p-4 flex items-center justify-between z-40 lg:hidden" data-testid="batch-footer-mobile">
          <p className="text-sm font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {selected.size} type{selected.size !== 1 ? 's' : ''} selected
          </p>
          <Button
            onClick={handleBatchCreate}
            disabled={batchLoading}
            className="bg-black text-white gap-2 rounded-full px-5"
            data-testid="batch-generate-mobile-btn"
          >
            {batchLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
            Generate All
          </Button>
        </div>
      )}
    </div>
  );
};

const AssetCard = ({ asset, isSelected, isLoading, onToggle, onCreate }) => {
  const Icon = asset.icon;

  return (
    <div
      className={`relative group rounded-2xl border-2 transition-all duration-200 overflow-hidden cursor-pointer hover:-translate-y-1 hover:shadow-lg ${
        isSelected
          ? 'border-black bg-gray-50 shadow-md ring-2 ring-black/10'
          : 'border-gray-200 bg-white hover:border-gray-300'
      }`}
      data-testid={`asset-card-${asset.id}`}
    >
      {/* Selection checkbox */}
      <button
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        className={`absolute top-3 right-3 z-10 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${
          isSelected
            ? 'bg-black border-black'
            : 'border-gray-300 bg-white/80 backdrop-blur-sm group-hover:border-gray-400'
        }`}
        data-testid={`asset-checkbox-${asset.id}`}
      >
        {isSelected && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
      </button>

      {/* Card body - click to create */}
      <div onClick={onCreate} className="p-6 pb-4">
        {/* Icon */}
        <div
          className={`w-14 h-14 rounded-xl bg-gradient-to-br ${asset.gradient} flex items-center justify-center mb-4 shadow-sm group-hover:shadow-md transition-shadow`}
        >
          <Icon className="h-7 w-7 text-white" />
        </div>

        {/* Text */}
        <h4 className="text-base font-bold tracking-tight mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {asset.label}
        </h4>
        <p className="text-xs text-gray-500 leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {asset.desc}
        </p>
      </div>

      {/* Action row */}
      <div className="px-6 pb-5">
        <button
          onClick={(e) => { e.stopPropagation(); onCreate(); }}
          disabled={isLoading}
          className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all ${
            isLoading
              ? 'bg-gray-100 text-gray-400'
              : `${asset.bgLight} hover:opacity-80`
          }`}
          style={{ color: isLoading ? undefined : asset.color, fontFamily: 'Manrope, sans-serif' }}
          data-testid={`create-${asset.id}-btn`}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Selecting template...
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4" />
              Create {asset.label}
              <ArrowRight className="h-3.5 w-3.5 opacity-60" />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default AssetTypeHub;
